import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Alert,
  CircularProgress, TextField, Button, Link
} from '@mui/material';
import { useAuth } from './AuthContext';
import sabalLogo from '../assets/Asset 4@2x hi res.png';
import sabalLogoMain from '../assets/sabal_logo.png';

const HANKO_API_URL = 'https://hanko-ji2csljmda-uk.a.run.app';
const BACKEND_URL = 'https://sabalsql-dbam-ji2csljmda-uk.a.run.app';

// Bound both the request and response body so an auth outage leaves a retryable form.
async function fetchAuthJson(url, options = {}) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    if (response.status >= 500) {
      throw new Error('Sign-in service is temporarily unavailable. Please try again.');
    }
    const data = await response.json();
    // Hanko can return a flow error on a 4xx response; let the step show it.
    if (!response.ok && !data.name) {
      throw new Error(data.message || 'Could not complete sign-in. Please try again.');
    }
    return { data, token: response.headers.get('X-Auth-Token') };
  } catch (err) {
    if (controller.signal.aborted) {
      throw new Error('Sign-in service timed out. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

// Stores the most recent X-Auth-Token received from any Hanko Flow API call.
// Updated on every hankoFlow() response that includes the header.
let lastAuthToken = null;

// ---------------------------------------------------------------------------
// Hanko Flow API helper
// ---------------------------------------------------------------------------
// Each Hanko flow call returns { name, csrf_token, actions, payload, error }.
// To advance, POST to an action's href with { csrf_token, input_data }.
// Also captures the X-Auth-Token response header (the session JWT) whenever
// Hanko sends it — typically on the final "success" step.
// ---------------------------------------------------------------------------
async function hankoFlow(path, csrfToken, inputData) {
  const body = {};
  if (csrfToken) body.csrf_token = csrfToken;
  if (inputData) body.input_data = inputData;

  const { data, token } = await fetchAuthJson(`${HANKO_API_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });

  if (token) lastAuthToken = token;

  return data;
}

// Start a flow and immediately send client capabilities (the mandatory first step).
async function startFlow(flowPath) {
  const step1 = await hankoFlow(flowPath, null, null);
  if (step1.name === 'error') return step1;

  const capAction = step1.actions?.register_client_capabilities;
  if (!capAction) return step1; // unexpected; return as-is

  const webauthnAvailable = !!(
    window.PublicKeyCredential &&
    navigator.credentials?.create &&
    navigator.credentials?.get
  );
  let platformAvailable = false;
  try {
    platformAvailable = webauthnAvailable &&
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (_) {}

  return hankoFlow(capAction.href, step1.csrf_token, {
    webauthn_available: webauthnAvailable,
    webauthn_conditional_mediation_available: false,
    webauthn_platform_authenticator_available: platformAvailable
  });
}

// ---------------------------------------------------------------------------
// Steps (states) of our custom flow
// ---------------------------------------------------------------------------
// email           – enter email address
// checking        – calling backend to see if user exists in Hanko
// reg_password    – new user: enter + confirm password
// reg_passcode    – new user: verify email with passcode
// login_password  – existing user: enter password
// login_passcode  – existing user: verify email passcode after password
// error           – something went wrong
// ---------------------------------------------------------------------------

export default function LoginPage() {
  const { signInWithHanko, status } = useAuth();
  const hankoRef = useRef(null);       // Hanko SDK instance (for session detection)

  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passcode, setPasscode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Hanko flow state carried between steps
  const flowRef = useRef({ csrf: '', actions: {} });

  // Lazily init Hanko SDK (for session token reading after flow completes).
  // We do NOT use onSessionCreated here — our custom flow handlers call
  // signInWithHanko(lastAuthToken) directly after a successful Hanko Flow API completion.
  useEffect(() => {
    fetch(`${HANKO_API_URL}/.well-known/jwks.json`, { method: 'GET', mode: 'cors' }).catch(() => {});
    (async () => {
      try {
        const { Hanko } = await import('@teamhanko/hanko-elements');
        hankoRef.current = new Hanko(HANKO_API_URL);
      } catch (e) {
        console.error('Hanko SDK init error:', e);
      }
    })();
  }, []);

  const completeSignIn = useCallback(async () => {
    try {
      const result = await signInWithHanko(lastAuthToken);
      if (!result?.ok) setError(result?.error?.message || 'Sign-in could not be completed. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [signInWithHanko]);

  // ------- Step 1: email submitted -------
  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!addr || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError('');
    setBusy(true);
    setStep('checking');
    lastAuthToken = null;

    try {
      // Ask the backend whether Hanko knows this email
      const { data: { exists } } = await fetchAuthJson(`${BACKEND_URL}/api/auth/user-status?email=${encodeURIComponent(addr)}`);

      if (exists) {
        // ---- EXISTING user → login flow ----
        const state = await startFlow('/login');
        if (state.name === 'error') throw new Error(state.error?.message || 'Login flow failed.');

        const identAction = state.actions?.continue_with_login_identifier;
        if (!identAction) throw new Error('Unexpected login flow state.');

        const afterIdent = await hankoFlow(identAction.href, state.csrf_token, { email: addr });
        if (afterIdent.name === 'error') throw new Error(afterIdent.error?.message || 'Login flow failed.');

        // Hanko returns login_method_chooser; auto-select password
        const pwAction = afterIdent.actions?.continue_to_password_login;
        if (!pwAction) throw new Error('Password login not available for this account.');

        const pwState = await hankoFlow(pwAction.href, afterIdent.csrf_token, {});
        if (pwState.name === 'error') throw new Error(pwState.error?.message || 'Login flow failed.');

        flowRef.current = { csrf: pwState.csrf_token, actions: pwState.actions };
        setBusy(false);
        setStep('login_password');
      } else {
        // ---- NEW user → registration flow ----
        const state = await startFlow('/registration');
        if (state.name === 'error') throw new Error(state.error?.message || 'Registration flow failed.');

        const emailAction = state.actions?.register_login_identifier;
        if (!emailAction) throw new Error('Unexpected registration flow state.');

        // Send email to Hanko
        const afterEmail = await hankoFlow(emailAction.href, state.csrf_token, { email: addr });
        if (afterEmail.name === 'error') throw new Error(afterEmail.error?.message || 'Registration failed.');

        console.log('[SAC] afterEmail flow state:', afterEmail.name, 'actions:', Object.keys(afterEmail.actions || {}));

        // Store state for the next step.
        // Hanko may go to passcode verification first (email verify) or to method chooser.
        flowRef.current = { csrf: afterEmail.csrf_token, actions: afterEmail.actions };

        // If Hanko asks for a passcode (email verification), show that step.
        if (afterEmail.actions?.verify_passcode_code || afterEmail.actions?.verify_passcode) {
          setBusy(false);
          setStep('reg_passcode');
        }
        // If Hanko goes to a method/credential chooser, navigate to the password step.
        else {
          // Search for any password-related action in the flow state
          const actionKeys = Object.keys(afterEmail.actions || {});
          const pwActionKey = actionKeys.find(k => k.includes('password'));
          let currentState = afterEmail;

          if (pwActionKey) {
            console.log('[SAC] found password action:', pwActionKey);
            // If it's a "continue_to" action, we need to navigate to it first
            if (pwActionKey.startsWith('continue_to')) {
              const navResult = await hankoFlow(afterEmail.actions[pwActionKey].href, afterEmail.csrf_token, {});
              console.log('[SAC] navigated to password step:', navResult.name, 'actions:', Object.keys(navResult.actions || {}));
              flowRef.current = { csrf: navResult.csrf_token, actions: navResult.actions };
              currentState = navResult;
            }
          } else {
            console.log('[SAC] no password action found, available actions:', actionKeys);
          }

          setBusy(false);
          setStep('reg_password');
        }
      }
    } catch (err) {
      console.error('[SAC] auth flow error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setBusy(false);
      setStep('email');
    }
  }, [email]);

  // ------- Registration: set password -------
  const handleRegPassword = useCallback(async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setError('');
    setBusy(true);

    try {
      const { csrf, actions } = flowRef.current;
      console.log('[SAC] handleRegPassword actions:', Object.keys(actions || {}));

      // Look for any password registration action (Hanko action names vary by version/config)
      const actionKeys = Object.keys(actions || {});
      const pwActionKey = actionKeys.find(k =>
        k === 'register_password' || k === 'password_register' ||
        k === 'password_creation' || k === 'set_password' ||
        (k.includes('password') && !k.includes('login'))
      );

      if (pwActionKey) {
        console.log('[SAC] submitting password via action:', pwActionKey);
        const pwAction = actions[pwActionKey];
        const result = await hankoFlow(pwAction.href, csrf, { new_password: password });
        if (result.name === 'error') throw new Error(result.error?.message || 'Password registration failed.');
        flowRef.current = { csrf: result.csrf_token, actions: result.actions };
        console.log('[SAC] after password submit:', result.name, 'actions:', Object.keys(result.actions || {}));

        // If Hanko now wants email passcode verification:
        if (result.actions?.verify_passcode_code || result.actions?.verify_passcode) {
          setBusy(false);
          setStep('reg_passcode');
          return;
        }

        // If flow completed (session created), the onSessionCreated callback fires.
        // Some flows may need us to explicitly call signInWithHanko.
        if (result.name === 'success' || result.name === 'login_success') {
          await completeSignIn();
          return;
        }

        // Otherwise store state and see what's next.
        // If there's a skip action (e.g. skip passkey setup), take it.
        if (result.actions?.skip) {
          const skipResult = await hankoFlow(result.actions.skip.href, result.csrf_token, {});
          flowRef.current = { csrf: skipResult.csrf_token, actions: skipResult.actions };
          if (skipResult.name === 'success' || skipResult.name === 'login_success') {
            await completeSignIn();
            return;
          }
        }

        setBusy(false);
        return;
      }

      console.error('[SAC] no password action found. Available actions:', actionKeys);
      throw new Error(`Password registration action not available. Available: [${actionKeys.join(', ')}]`);
    } catch (err) {
      console.error('[SAC] reg password error:', err);
      setError(err.message || 'Password registration failed.');
      setBusy(false);
    }
  }, [password, passwordConfirm, completeSignIn]);

  // ------- Passcode verification (both reg and login) -------
  const handlePasscodeSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!passcode.trim()) {
      setError('Please enter the code sent to your email.');
      return;
    }

    setError('');
    setBusy(true);

    try {
      const { csrf, actions } = flowRef.current;
      const verifyAction = actions?.verify_passcode_code || actions?.verify_passcode;
      if (!verifyAction) throw new Error('Passcode verification not available.');

      const result = await hankoFlow(verifyAction.href, csrf, { code: passcode.trim() });
      if (result.name === 'error') {
        const msg = result.error?.message || 'Invalid code.';
        // Keep the user on the passcode step so they can retry.
        setError(msg);
        setBusy(false);
        return;
      }

      flowRef.current = { csrf: result.csrf_token, actions: result.actions };

      // After passcode, Hanko may want to set a password (registration) or complete (login).
      if (result.name === 'success' || result.name === 'login_success') {
        await completeSignIn();
        return;
      }

      // If it wants a password next (registration flow):
      if (result.actions?.register_password || result.actions?.continue_to_password_registration) {
        if (result.actions.continue_to_password_registration) {
          const pwState = await hankoFlow(result.actions.continue_to_password_registration.href, result.csrf_token, {});
          flowRef.current = { csrf: pwState.csrf_token, actions: pwState.actions };
        }
        setBusy(false);
        setStep('reg_password');
        return;
      }

      // Skip passkey/onboarding steps if offered
      if (result.actions?.skip) {
        const skipResult = await hankoFlow(result.actions.skip.href, result.csrf_token, {});
        flowRef.current = { csrf: skipResult.csrf_token, actions: skipResult.actions };
        if (skipResult.name === 'success' || skipResult.name === 'login_success') {
          await completeSignIn();
          return;
        }
      }

      setBusy(false);
    } catch (err) {
      console.error('[SAC] passcode error:', err);
      setError(err.message || 'Verification failed.');
      setBusy(false);
    }
  }, [passcode, completeSignIn]);

  // ------- Login: submit password -------
  const handleLoginPassword = useCallback(async (e) => {
    e.preventDefault();
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setError('');
    setBusy(true);

    try {
      const { csrf, actions } = flowRef.current;
      const pwAction = actions?.password_login;
      if (!pwAction) throw new Error('Password login action not available.');

      const result = await hankoFlow(pwAction.href, csrf, { password });
      if (result.name === 'error') {
        setError(result.error?.message || 'Invalid password.');
        setBusy(false);
        return;
      }

      flowRef.current = { csrf: result.csrf_token, actions: result.actions };

      // Success → session created
      if (result.name === 'success' || result.name === 'login_success') {
        await completeSignIn();
        return;
      }

      // Hanko may ask for passcode verification (email 2FA) after password:
      if (result.actions?.verify_passcode_code || result.actions?.verify_passcode) {
        setBusy(false);
        setPasscode('');
        setStep('login_passcode');
        return;
      }

      // Skip onboarding steps if offered
      if (result.actions?.skip) {
        const skipResult = await hankoFlow(result.actions.skip.href, result.csrf_token, {});
        if (skipResult.name === 'success' || skipResult.name === 'login_success') {
          await completeSignIn();
          return;
        }
      }

      // Fallback
      await completeSignIn();
    } catch (err) {
      console.error('[SAC] login password error:', err);
      setError(err.message || 'Login failed.');
      setBusy(false);
    }
  }, [password, completeSignIn]);

  // ------- Back to email step -------
  const handleBack = useCallback(() => {
    setStep('email');
    setPassword('');
    setPasswordConfirm('');
    setPasscode('');
    setError('');
    flowRef.current = { csrf: '', actions: {} };
  }, []);

  // ------- Render -------
  const renderStep = () => {
    if (step === 'checking' || busy) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (step === 'email') {
      return (
        <form onSubmit={handleEmailSubmit}>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              size="small"
            />
            <Button type="submit" variant="contained" fullWidth
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Continue
            </Button>
          </Stack>
        </form>
      );
    }

    if (step === 'reg_password') {
      return (
        <form onSubmit={handleRegPassword}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Set up your password for <strong>{email}</strong>
            </Typography>
            <TextField
              label="Password"
              type="password"
              fullWidth
              autoFocus
              value={password}
              onChange={e => setPassword(e.target.value)}
              size="small"
              inputProps={{ minLength: 8 }}
            />
            <TextField
              label="Confirm password"
              type="password"
              fullWidth
              value={passwordConfirm}
              onChange={e => setPasswordConfirm(e.target.value)}
              size="small"
            />
            <Button type="submit" variant="contained" fullWidth
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Create account
            </Button>
            <Link component="button" variant="body2" onClick={handleBack} underline="hover">
              Back
            </Link>
          </Stack>
        </form>
      );
    }

    if (step === 'reg_passcode' || step === 'login_passcode') {
      return (
        <form onSubmit={handlePasscodeSubmit}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              A verification code was sent to <strong>{email}</strong>. Enter it below.
            </Typography>
            <TextField
              label="Verification code"
              fullWidth
              autoFocus
              value={passcode}
              onChange={e => setPasscode(e.target.value)}
              size="small"
              autoComplete="one-time-code"
            />
            <Button type="submit" variant="contained" fullWidth
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Verify
            </Button>
            <Link component="button" variant="body2" onClick={handleBack} underline="hover">
              Back
            </Link>
          </Stack>
        </form>
      );
    }

    if (step === 'login_password') {
      return (
        <form onSubmit={handleLoginPassword}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enter your password for <strong>{email}</strong>
            </Typography>
            <TextField
              label="Password"
              type="password"
              fullWidth
              autoFocus
              value={password}
              onChange={e => setPassword(e.target.value)}
              size="small"
            />
            <Button type="submit" variant="contained" fullWidth
              sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}>
              Sign in
            </Button>
            <Link component="button" variant="body2" onClick={handleBack} underline="hover">
              Back
            </Link>
          </Stack>
        </form>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        height: '100vh',
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        px: 2,
        bgcolor: '#ffffff',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${sabalLogo})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'min(92vw, 1100px)',
          opacity: 0.22,
          mixBlendMode: 'screen',
          filter: 'contrast(1.05) saturate(1.05)',
          pointerEvents: 'none'
        }
      }}
    >
      <Box sx={{ position: 'absolute', left: '50%', top: '55%', transform: 'translate(-50%, -50%)', zIndex: 1, width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <img src={sabalLogoMain} alt="Sabal" style={{ width: 120 }} />
        </Box>
        <Card
          variant="outlined"
          sx={{
            width: '100%',
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(255,255,255,0.92)'
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Sabal Administrative Console</Typography>
              </Box>

              {(status.state === 'error' || error) && (
                <Alert severity="error">{error || status.message || 'Sign-in failed'}</Alert>
              )}

              {renderStep()}

              <Typography variant="caption" color="text.secondary">
                Session is managed securely. You will stay signed in until your session expires.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 14,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          px: 2
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.55)', textAlign: 'center' }}>
          www.sabalcanopy.com
        </Typography>
      </Box>
    </Box>
  );
}
